import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOrg } from './OrgContext';
import { Avatar } from './AppNav';

// Who you are, which site you're in, and - when you can reach more than one -
// where you switch between them. It opens from your initials at the right of the
// top bar.
//
// **On a desk that is all it holds**, the owner's instruction of 3 October 2026:
// "it should only be the site picker, user details and sign out." It used to list
// the Settings pages too, which is what the brand guide's 11.7 asks for, and that
// stopped making sense the moment Settings went back to being an area in the bar:
// the same menu was in two places at once.
//
// `settings` is still a prop and still works, because the sheet below 1024 is a
// different component and Settings really is only reachable from your initials
// there. On a desk the bar passes null.
//
// The menu is portalled to the page rather than drawn inside the bar, because
// the bar clips its own contents (that's what keeps the tier backgrounds inside
// its rounded corners). Position is measured from the button each time it opens.

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function UserMenu({ userName, roleLabel, onSignOut, settings, activeKey, onSelect, lit = false }) {
  const { sites, current, hasMultiple, switchTo, switching, error } = useOrg();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // Removed on close as well as after it fires, so opening the menu ten
    // times does not leave ten listeners behind (the audit after the rebrand,
    // 19 September 2026).
    const onResize = () => setOpen(false);
    window.addEventListener('resize', onResize, { once: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const menu = open && pos ? createPortal(
    <div
      ref={menuRef}
      className="um-menu"
      style={{ top: pos.top, right: pos.right }}
      role="menu"
    >
      <div className="um-head">
        <div className="um-head-name">{userName}</div>
        <div className="um-head-role">{roleLabel}</div>
        {current && <div className="um-head-site">{current.name}</div>}
      </div>

      {settings && settings.pages.length > 0 && (
        <>
          <div className="um-sep" />
          <div className="um-label">Settings</div>
          {settings.pages.map(p => {
            const on = p.key === activeKey;
            return (
              <button
                key={p.key}
                className={'um-item' + (on ? ' on' : '')}
                aria-current={on ? 'page' : undefined}
                onClick={() => { setOpen(false); if (onSelect) onSelect(p.key); }}
                role="menuitem"
              >
                <span className="um-check">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p.icon}</svg>
                </span>
                <span className="um-item-label">{p.label}</span>
              </button>
            );
          })}
        </>
      )}
      <div className="um-sep" />

      {hasMultiple && (
        <>
          <div className="um-label">Your sites</div>
          {sites.map(s => {
            const isCurrent = current && s.id === current.id;
            return (
              <button
                key={s.id}
                className={'um-item' + (isCurrent ? ' on' : '')}
                disabled={switching}
                onClick={() => { if (!isCurrent) switchTo(s.id); }}
                role="menuitem"
              >
                <span className="um-check">{isCurrent && <CheckIcon />}</span>
                <span className="um-item-label">{s.name}</span>
                {/* Three full stops, not the one-character ellipsis. The brand guide's
                    section 0 rule 12 asks for plain ASCII in every delivered
                    file, and this was the only place in OnHand's navigation
                    that broke it. */}
                {switching && !isCurrent && <span className="um-item-note">switching...</span>}
              </button>
            );
          })}
          {error && <div className="um-error">{error}</div>}
          <div className="um-sep" />
        </>
      )}

      <button className="um-item danger" onClick={onSignOut} role="menuitem">
        <span className="um-check">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </span>
        <span className="um-item-label">Sign Out</span>
      </button>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        className={'um-trigger' + (open ? ' open' : '') + (lit ? ' on' : '')}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={'Your account and settings' + (userName ? ', ' + userName : '') + (current ? ', ' + current.name : '')}
        title={[userName, current ? current.name : roleLabel].filter(Boolean).join(' - ')}
      >
        <Avatar name={userName} size={40} />
      </button>
      {menu}
    </>
  );
}
